import {
    durationToMinutes,
    minutesToDuration,
    numericInputProps,
    toApiDateTime,
    toInputDateTime,
} from './jobs';

export const MAINTENANCE_TYPES = [
    { value: 'manutencao_corretiva', label: 'Manutenção corretiva' },
    { value: 'manutencao_preventiva', label: 'Manutenção preventiva' },
    { value: 'limpeza', label: 'Limpeza' },
    { value: 'erro_em_investigacao', label: 'Erro em investigação' },
];

export const DEFAULT_MAINTENANCE_DURATION = '00:00';

export { durationToMinutes, minutesToDuration, numericInputProps };

export function maintenanceTypeLabel(value) {
    const found = MAINTENANCE_TYPES.find((type) => type.value === value);
    return found ? found.label : value || '—';
}

export function getMaintenanceTypeBadgeProps(type) {
    const config = {
        manutencao_corretiva: { color: 'error' },
        manutencao_preventiva: { color: 'info' },
        limpeza: { color: 'success' },
        erro_em_investigacao: { color: 'warning' },
    }[type] || { color: 'default' };

    return {
        label: maintenanceTypeLabel(type),
        size: 'small',
        color: config.color,
    };
}

/**
 * Agrega manutenções por impressora (soma análise + correção em minutos).
 * Ordenação exclusiva por downtime total — quantidade de ocorrências não define a posição.
 */
export function buildMaintenanceSummaryCards(printers, maintenances) {
    const summary = {};
    (maintenances || []).forEach((row) => {
        const printerId = row.printer_uuid;
        if (!printerId) {
            return;
        }
        if (!summary[printerId]) {
            summary[printerId] = { count: 0, downtimeMinutes: 0 };
        }
        summary[printerId].count += 1;
        const analysis = parseInt(row.analysis_duration_minutes, 10) || 0;
        const correction = parseInt(row.correction_duration_minutes, 10) || 0;
        summary[printerId].downtimeMinutes += analysis + correction;
    });

    return (printers || [])
        .map((printer) => {
            const stats = summary[printer.id] || { count: 0, downtimeMinutes: 0 };
            return {
                printer_uuid: printer.id,
                name: printer.name,
                brand: printer.brand,
                maintenance_count: stats.count,
                downtime_minutes: stats.downtimeMinutes,
            };
        })
        .sort((a, b) => b.downtime_minutes - a.downtime_minutes);
}

export function buildPrinterMaintenanceListQuery({ page, limit, printerUuid, type }) {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (printerUuid) {
        params.set('printer_uuid', printerUuid);
    }
    if (type) {
        params.set('type', type);
    }
    return params.toString();
}

export function partsToRows(parts) {
    if (!Array.isArray(parts)) {
        return [''];
    }

    const rows = parts
        .map((part) => (typeof part === 'string' ? part : part?.name ?? ''))
        .map((part) => String(part).trim())
        .filter(Boolean);

    return rows.length ? rows : [''];
}

export function rowsToParts(rows) {
    return rows.map((part) => String(part).trim()).filter(Boolean);
}

export function maintenanceFromApi(data) {
    return {
        printer_uuid: data.printer_uuid || '',
        occurred_at: toInputDateTime(data.occurred_at),
        type: data.type || 'manutencao_preventiva',
        problem_description: data.problem_description || '',
        analysis_duration: minutesToDuration(data.analysis_duration_minutes ?? 0),
        performed_actions: data.performed_actions || '',
        printer_hours_count: data.printer_hours_count ?? 0,
        correction_duration: minutesToDuration(data.correction_duration_minutes ?? 0),
    };
}

export function buildMaintenancePayload(form, replacementParts) {
    const payload = {
        printer_uuid: form.printer_uuid,
        type: form.type,
        problem_description: form.problem_description,
        analysis_duration_minutes: durationToMinutes(form.analysis_duration) ?? 0,
        performed_actions: form.performed_actions,
        printer_hours_count: parseInt(form.printer_hours_count, 10) || 0,
        replacement_parts: rowsToParts(replacementParts),
        correction_duration_minutes: durationToMinutes(form.correction_duration) ?? 0,
    };

    const occurredAt = toApiDateTime(form.occurred_at);
    if (occurredAt) {
        payload.occurred_at = occurredAt;
    }

    return payload;
}
